import { AbstractTranscriptEditorCommand } from "./AbstractTranscriptEditorCommand";
import { TranscriptEditorCore } from "../TranscriptEditorCore";
import { TranscriptEditorChangeType } from "../types";

/**
 * Command to adjust the start time of a transcript element
 * 
 * This command changes the start timestamp of a text element in the transcript,
 * which affects audio alignment. It stores the old time for undo operations.
 */
export class AdjustElementStartCommand extends AbstractTranscriptEditorCommand {
  /** ID of the segment containing the element */
  private segmentId: string;
  
  /** ID of the paragraph containing the element */
  private paragraphId: string;
  
  /** ID of the element to adjust */
  private elementId: string;
  
  /** Original start time for undo operations */
  private oldStartTime: number;
  
  /** New start time to set */
  private newStartTime: number;
  
  /**
   * Creates a new AdjustElementStartCommand
   * 
   * @param editor Reference to the editor core
   * @param segmentId ID of the segment containing the element
   * @param paragraphId ID of the paragraph containing the element
   * @param elementId ID of the element to adjust
   * @param oldStartTime Original start time
   * @param newStartTime New start time
   */
  constructor(
    editor: TranscriptEditorCore,
    segmentId: string,
    paragraphId: string,
    elementId: string,
    oldStartTime: number,
    newStartTime: number
  ) {
    super(`Adjust start time to ${newStartTime.toFixed(2)}s`, editor);
    
    this.segmentId = segmentId;
    this.paragraphId = paragraphId;
    this.elementId = elementId;
    this.oldStartTime = oldStartTime;
    this.newStartTime = newStartTime;
    
    // Store metadata about the timing adjustment
    this.setMetadata('elementId', elementId);
    this.setMetadata('oldStartTime', oldStartTime);
    this.setMetadata('newStartTime', newStartTime);
    this.setMetadata('timeChange', newStartTime - oldStartTime);
  }
  
  /**
   * Execute the command
   * 
   * @returns Promise resolving to true if successful
   */
  async execute(): Promise<boolean> {
    console.log('AdjustElementStartCommand execute')
    try {
      // Get the container
      const container = this.editor.getContainer();
      if (!container) {
        console.error('No container available');
        return false;
      }
      
      // Get the element
      const segment = container.getSegment(this.segmentId);
      if (!segment) {
        console.error(`Segment not found: ${this.segmentId}`);
        return false;
      }
      
      const paragraph = segment.getParagraph(this.paragraphId);
      if (!paragraph) {
        console.error(`Paragraph not found: ${this.paragraphId}`);
        return false;
      }
      
      const element = paragraph.getElement(this.elementId);
      if (!element || element.type !== 'text') {
        console.error(`Text element not found: ${this.elementId}`);
        return false;
      }
      console.log('element', element)
      // Update the start time
      const textElement = element as any; // Using any to avoid TypeScript errors with property assignment
      textElement.startTime = this.newStartTime;
      
      // Notify listeners about the change
      this.editor.notifyObservers(TranscriptEditorChangeType.ELEMENT_UPDATED, {
        segmentId: this.segmentId,
        paragraphId: this.paragraphId,
        elementId: this.elementId,
        element
      });
      
      return true;
    } catch (error) {
      console.error('Error executing AdjustElementStartCommand:', error);
      
      return false;
    }
  }
  
  /**
   * Undo the command
   * 
   * @returns Promise resolving to true if successful
   */
  async undo(): Promise<boolean> {
    
    try {
      // Get the container
      const container = this.editor.getContainer();
      if (!container) {
        console.error('No container available');
        return false;
      }
      
      // Get the element
      const segment = container.getSegment(this.segmentId);
      if (!segment) {
        console.error(`Segment not found: ${this.segmentId}`);
        return false;
      }
      
      const paragraph = segment.getParagraph(this.paragraphId);
      if (!paragraph) {
        console.error(`Paragraph not found: ${this.paragraphId}`);
        return false;
      }
      
      const element = paragraph.getElement(this.elementId);
      if (!element || element.type !== 'text') {
        console.error(`Text element not found: ${this.elementId}`);
        return false;
      }
      
      // Restore the original start time
      const textElement = element as any; // Using any to avoid TypeScript errors with property assignment
      textElement.startTime = this.oldStartTime;
      
      // Notify listeners about the change
      this.editor.notifyObservers(TranscriptEditorChangeType.ELEMENT_UPDATED, {
        segmentId: this.segmentId,
        paragraphId: this.paragraphId,
        elementId: this.elementId,
        element
      });
      
      return true;
    } catch (error) {
      console.error('Error undoing AdjustElementStartCommand:', error);
      
      return false;
    }
  }
}